import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { formatDataGeneral, getAttributeValue, getFileLinkIfExist } from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete, scoreOnlyIfRowEvidenceComplete } from "utils/renderDpatEvidenceLink";
import { filterRecordsByDpatYearAttribute, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const BILLING_PROGRAM = "z20IsSBulcq";
const BILLING_SYSTEM_TYPE = "Billing";
const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const IGF_PROGRAM = "dYNmYGtArrK";
const IGF_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const IGF_AMOUNT_COLLECTED_ATTRIBUTE = "nlZbT98zSio";
const IGF_TRACKER_ENTITY_FIELDS =
    "trackedEntity,attributes[attribute,displayName,value]";
const SYSTEM_TYPE_DISPLAY_NAMES = ["System Type", "DPAT | System Type", "TEI | System Type"];
const REPORT_DATA_ELEMENT = "hM6AUNKRbKB";
const TRACKER_ENTITIES_PAGE_SIZE = 5000;
const TRACKER_EVENTS_PAGE_SIZE = 10000;
const TRACKER_ENTITY_FIELDS =
    "trackedEntity,attributes[attribute,displayName,value],enrollments[program,attributes[attribute,displayName,value]]";

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function normalizeAttributeLabel(label) {
    return String(label || "")
        .toLowerCase()
        .replace(/^\s*(dpat|tei)\s*\|\s*/i, "")
        .trim();
}

function normalizeSystemTypeValue(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
}

function matchesBillingSystemType(value) {
    const normalized = normalizeSystemTypeValue(value);
    if (!normalized) {
        return false;
    }

    return normalized === "billing" || normalized.includes("billing");
}

function collectSoftwareAttributes(instance) {
    const attributes = [...(instance?.attributes || [])];

    (instance?.enrollments || []).forEach((enrollment) => {
        if (!enrollment?.program || enrollment.program === BILLING_PROGRAM) {
            attributes.push(...(enrollment?.attributes || []));
        }
    });

    return attributes;
}

function isSystemTypeAttribute(attribute) {
    if (!attribute) {
        return false;
    }

    const label = normalizeAttributeLabel(attribute.displayName);
    if (label === "system type") {
        return true;
    }

    return /system\s*type/i.test(String(attribute.displayName || ""));
}

function getSystemTypeValue(instance) {
    const attributes = collectSoftwareAttributes(instance);

    for (const name of SYSTEM_TYPE_DISPLAY_NAMES) {
        const value = getAttributeValue(name, { attributes });
        if (value && value !== "N/A") {
            return value;
        }
    }

    const matchedAttribute = attributes.find(isSystemTypeAttribute);
    return matchedAttribute?.value || "";
}

function filterBillingSoftware(instances = []) {
    const displayNameMatches = SYSTEM_TYPE_DISPLAY_NAMES.flatMap((name) =>
        formatDataGeneral(instances, name, BILLING_SYSTEM_TYPE) || [],
    );

    if (displayNameMatches.length) {
        const uniqueMatches = new Map(
            displayNameMatches.map((instance) => [instance.trackedEntity, instance]),
        );
        return [...uniqueMatches.values()];
    }

    return instances.filter((instance) => matchesBillingSystemType(getSystemTypeValue(instance)));
}

function parseAmount(value) {
    if (value == null || value === "" || value === "N/A") {
        return 0;
    }

    const parsed = parseFloat(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatGhanaCedis(amount) {
    const value = parseAmount(amount);
    return `GH₵ ${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function getAttributeWithFallback(unit, displayNames, attributeId) {
    for (const name of displayNames) {
        const value = getAttributeValue(name, unit);
        if (value && value !== "N/A") {
            return value;
        }
    }

    const matched = unit?.attributes?.find((attr) => attr.attribute === attributeId);
    const value = matched?.value;
    return value == null || value === "" ? "N/A" : value;
}

function getIgfYear(record) {
    return getAttributeWithFallback(
        record,
        ["Year", "Select Year", "DPAT | Year"],
        IGF_YEAR_ATTRIBUTE,
    );
}

function getIgfAmountCollected(record) {
    return getAttributeWithFallback(
        record,
        [
            "Amount Collected",
            "Amt Collected",
            "DPAT | Amount Collected",
            "Amount collected",
        ],
        IGF_AMOUNT_COLLECTED_ATTRIBUTE,
    );
}

function filterIgfRecordsByYear(records = [], targetYear) {
    if (isDpatScoreRelaxationExempt()) {
        return filterRecordsByDpatYearAttribute(records, targetYear);
    }
    const yearStr = String(targetYear);
    if (!yearStr || yearStr === "undefined" || yearStr === "null") {
        return records;
    }

    const displayNameMatches = [
        formatDataGeneral(records, "Year", yearStr),
        formatDataGeneral(records, "Select Year", yearStr),
        formatDataGeneral(records, "DPAT | Year", yearStr),
    ].find((matches) => matches?.length);

    if (displayNameMatches?.length) {
        return displayNameMatches;
    }

    return records.filter((record) => String(getIgfYear(record)) === yearStr);
}

async function fetchIgfRecordsForYear(orgUnit, targetYear) {
    const params = {
        orgUnit,
        program: IGF_PROGRAM,
        pageSize: TRACKER_ENTITIES_PAGE_SIZE,
        fields: IGF_TRACKER_ENTITY_FIELDS,
    };
    if (!isDpatScoreRelaxationExempt()) {
        params.filter = `${IGF_YEAR_ATTRIBUTE}:EQ:${targetYear}`;
    }
    const response = await axios.get("/tracker/trackedEntities", { params });

    const instances = response?.data?.instances || [];
    const filtered = filterIgfRecordsByYear(instances, targetYear);
    return filtered.length ? filtered : instances;
}

function calculateTotalIgfCollected(ifgRecords = [], targetYear) {
    const yearRecords = filterIgfRecordsByYear(ifgRecords, targetYear);
    let total = 0;

    yearRecords.forEach((record) => {
        total += parseAmount(getIgfAmountCollected(record));
    });

    return total;
}

function buildRateableRevenueDocLinks(documents) {
    const documentData = documents?.data;
    const documentReports = documents?.reports;
    const documentTypes = [
        { type: "Auditor-General Reports", title: "Auditor-General Reports" },
        {
            type: "Internally Generated Fund Report (IGF)",
            title: "Internally Generated Fund Report (IGF)",
        },
    ];

    return documentTypes.map(({ type, title }) => {
        const matchedDocs = formatDataGeneral(
            documentData,
            "DPAT | M&E Tool Type",
            type,
        ) || [];

        let reportLink = "";
        if (matchedDocs.length > 0) {
            reportLink = getFileLinkIfExist(
                documentReports,
                DOCUMENT_HUB_FILE_ELEMENT,
                matchedDocs[0]?.trackedEntity,
            );
        }

        return {
            title,
            link: reportLink
                ? fileUrl(reportLink, DOCUMENT_HUB_FILE_ELEMENT)
                : "Not Uploaded",
        };
    });
}

function formatIgfTooltipValue(value) {
    return formatGhanaCedis(value);
}

const RateableRevenu = forwardRef(({
    year,
    district,
    documents,
    ifg,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {

    const { Header, Content } = Layout;
    const { Title, Text } = Typography;

    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const analyticsYear = Number(resolvedYear) || new Date().getFullYear();
    const previousYear = analyticsYear - 1;
    const orgUnit =
        typeof district === "string"
            ? district
            : district?.value?.id ?? district?.id ?? "";

    const [software, setSoftware] = useState([]);
    const [issuance, setIssuance] = useState([]);
    const [followup, setFollowup] = useState([]);
    const [documentLinks, setDocumentLinks] = useState([]);
    const [currentYearIgf, setCurrentYearIgf] = useState(0);
    const [previousYearIgf, setPreviousYearIgf] = useState(0);
    const [scorei, setScoreI] = useState(0);
    const [scoreii, setScoreII] = useState(0);
    const [scoreiii, setScoreIII] = useState(0);
    const [maxScore, setMaxScore] = useState(5);

    const liveDocumentLinks = useMemo(
        () => buildRateableRevenueDocLinks(documents),
        [documents],
    );

    const displayDocumentLinks = isAssessmentStarted && savedAssessmentData?.links?.length
        ? savedAssessmentData.links
        : documentLinks.length
            ? documentLinks
            : liveDocumentLinks;

    const DPAT_SLUG = "pi2.0-2.1";
    const hideIssuanceTable = analyticsYear === 2025;
    const showFollowUpTable = analyticsYear < 2025;

    const loadIgfComparison = useCallback(async () => {
        setDocumentLinks(liveDocumentLinks);

        let currentRecords = ifg?.data ?? [];
        let previousRecords = ifg?.data ?? [];

        if (orgUnit) {
            try {
                [currentRecords, previousRecords] = await Promise.all([
                    fetchIgfRecordsForYear(orgUnit, analyticsYear),
                    fetchIgfRecordsForYear(orgUnit, previousYear),
                ]);
            } catch (err) {
                console.error("RateableRevenu IGF fetch failed:", err);
                currentRecords = filterIgfRecordsByYear(ifg?.data ?? [], analyticsYear);
                previousRecords = filterIgfRecordsByYear(ifg?.data ?? [], previousYear);
            }
        } else {
            currentRecords = filterIgfRecordsByYear(currentRecords, analyticsYear);
            previousRecords = filterIgfRecordsByYear(previousRecords, previousYear);
        }

        const currentTotal = calculateTotalIgfCollected(currentRecords, analyticsYear);
        const previousTotal = calculateTotalIgfCollected(previousRecords, previousYear);

        setCurrentYearIgf(currentTotal);
        setPreviousYearIgf(previousTotal);

        // PI 2.0-2.1ii: Document Hub revenue reports (evidence of IGF collected).
        setScoreII(scoreOnlyIfDocumentLinksComplete(liveDocumentLinks, currentTotal > 0 ? 1 : 0));

        // PI 2.0-2.1iii: positive IGF growth (unchanged).
        setScoreIII(
            scoreOnlyIfDocumentLinksComplete(
                liveDocumentLinks,
                previousTotal < currentTotal ? 2 : 0,
            ),
        );
    }, [analyticsYear, previousYear, orgUnit, ifg, liveDocumentLinks]);

    const getBillingDetails = useCallback(() => {
        if (!orgUnit || resolvedYear == null) {
            setSoftware([]);
            setScoreI(0);
            return;
        }

        axios
            .get(
                `/tracker/trackedEntities?orgUnit=${orgUnit}&program=${BILLING_PROGRAM}&startDate=${analyticsYear}-01-01&endDate=${analyticsYear}-12-31&pageSize=${TRACKER_ENTITIES_PAGE_SIZE}&fields=${TRACKER_ENTITY_FIELDS}`,
            )
            .then((result) => {
                const systems = filterBillingSoftware(result.data?.instances || []);
                if (!systems.length) {
                    setSoftware([]);
                    setScoreI(0);
                    return;
                }

                axios
                    .get(
                        `/tracker/events?program=${BILLING_PROGRAM}&orgUnit=${orgUnit}&startDate=${analyticsYear}-01-01&endDate=${analyticsYear}-12-31&pageSize=${TRACKER_EVENTS_PAGE_SIZE}`,
                    )
                    .then((resp) => {
                        const reports = resp.data?.instances || [];
                        const temp = systems.map((system, index) => {
                            const reportLink = getFileLinkIfExist(
                                reports,
                                REPORT_DATA_ELEMENT,
                                system.trackedEntity,
                            );

                            return {
                                key: system.trackedEntity || index + 1,
                                billing: "YES",
                                functional: reportLink ? "YES" : "NO",
                                name: getAttributeValue("Name", system),
                                report: reportLink
                                    ? fileUrl(reportLink, REPORT_DATA_ELEMENT)
                                    : "Not Uploaded",
                            };
                        });

                        // PI 2.0-2.1i: computerized billing system exists with software report.
                        setScoreI(
                            scoreOnlyIfRowEvidenceComplete(
                                temp,
                                ["report"],
                                temp.length > 0 ? 2 : 0,
                            ),
                        );
                        setSoftware(temp);
                    })
                    .catch((err) => console.log(err));
            })
            .catch((err) => console.log(err));
    }, [orgUnit, resolvedYear, analyticsYear]);

    function getBillingData() {
        axios
            .get(`/tracker/trackedEntities?orgUnit=${orgUnit}&program=qSwpPQwR6Ku&startDate=${analyticsYear}-01-01&endDate=${analyticsYear}-12-31`)
            .then(result => {
                if (result.data.instances.length > 0) {

                    axios
                        .get(`/tracker/events?program=qSwpPQwR6Ku&orgUnit=${orgUnit}&startDate=${analyticsYear}-01-01&endDate=${analyticsYear}-12-31`)
                        .then(resp => {
                            const billings = result.data.instances;
                            const reports = resp.data.instances;

                            const propertyBillings = formatDataGeneral(billings, "DPAT | Billing Type", "Property Rate") || [];
                            const businessBillings = formatDataGeneral(billings, "DPAT | Billing Type", "Business Operating Permit") || [];

                            const propertyBill = propertyBillings.length;
                            let propertyBillIssued = 0;
                            const businessBill = businessBillings.length;
                            let businessBillIssued = 0;

                            propertyBillings.forEach(bill => {

                                const isIssued = getAttributeValue("Has it been issued?", bill);
                                const isDistributed = getAttributeValue("Bill Status", bill);

                                if (isIssued === "true" && isDistributed === "true") {
                                    propertyBillIssued++;
                                }
                            });

                            businessBillings.forEach(bill => {
                                const isIssued = getAttributeValue("Has it been issued?", bill);
                                const isDistributed = getAttributeValue("Bill Status", bill);

                                if (isIssued === "true" && isDistributed === "true") {
                                    businessBillIssued++;
                                }
                            });

                            const reportTemp = [];
                            reports.forEach((report, idx) => {
                                let actionType = "";
                                let actionDate = "";
                                let paymentDate = "";
                                const no = idx + 1;
                                report.dataValues.forEach(val => {
                                    if (val.dataElement === "bGzwdd1VsfI") {
                                        actionType = val.value;
                                    } else if (val.dataElement === "MmojpWRb4vB") {
                                        actionDate = val.value;
                                    } else if (val.dataElement === "lQ1VluGCriP") {
                                        paymentDate = val.value;
                                    }
                                });

                                if (report.dataValues.length > 0) {
                                    reportTemp.push({ actionType, actionDate, paymentDate, no });
                                }

                            });

                            const temp = [
                                {
                                    propertyBill,
                                    propertyBillIssued,
                                    businessBill,
                                    businessBillIssued,
                                    propertyBillDateSubmission: propertyBillings.length > 0 ? getAttributeValue("Date", propertyBillings[0]) : "",
                                    businessBillDateSubmission: businessBillings.length > 0 ? getAttributeValue("Date", businessBillings[0]) : ""
                                }
                            ];

                            setIssuance(temp);
                            setFollowup(showFollowUpTable ? reportTemp : []);

                        })
                        .catch(err => console.log(err))
                }

            })
            .catch(err => console.log(err))
    }

    const loadFromDhis2 = useCallback(() => {
        getBillingDetails();
        getBillingData();
        loadIgfComparison();
    }, [getBillingDetails, loadIgfComparison]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            if (snap.software) setSoftware(snap.software);
            if (snap.issuance) setIssuance(snap.issuance);
            if (snap.followup) setFollowup(snap.followup);
            if (snap.links) setDocumentLinks(snap.links);
            if (snap.currentYearIgf != null) setCurrentYearIgf(snap.currentYearIgf);
            if (snap.previousYearIgf != null) setPreviousYearIgf(snap.previousYearIgf);
            if (snap.scorei != null) setScoreI(snap.scorei);
            if (snap.scoreii != null) setScoreII(snap.scoreii);
            if (snap.scoreiii != null) setScoreIII(snap.scoreiii);
            if (snap.maxScore != null) setMaxScore(snap.maxScore);
        },
        deps: [analyticsYear, orgUnit, documents, ifg, loadFromDhis2],
    });

    const igfComparisonTableData = useMemo(() => ([
        {
            key: "previous-year-igf",
            year: previousYear,
            totalIgf: previousYearIgf,
            totalIgfDisplay: formatGhanaCedis(previousYearIgf),
        },
        {
            key: "current-year-igf",
            year: analyticsYear,
            totalIgf: currentYearIgf,
            totalIgfDisplay: formatGhanaCedis(currentYearIgf),
        },
    ]), [analyticsYear, previousYear, currentYearIgf, previousYearIgf]);

    const igfChartData = useMemo(() => ([
        { year: String(previousYear), total: previousYearIgf },
        { year: String(analyticsYear), total: currentYearIgf },
    ]), [analyticsYear, previousYear, currentYearIgf, previousYearIgf]);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI2",
            area: "Revenue Generation",
            maxScore,
            software,
            issuance,
            followup,
            links: displayDocumentLinks,
            igfComparison: igfComparisonTableData,
            currentYearIgf,
            previousYearIgf,
            scorei,
            scoreii,
            scoreiii
        }),
    }));

    const documentTableColumns = useMemo(
        () => documentsColumn.map((col) =>
            col.dataIndex === "link"
                ? {
                    ...col,
                    render: (value) => renderDpatEvidenceLink(value, "View Document"),
                }
                : col
        ),
        [],
    );

    const igfComparisonColumns = [
        {
            title: "Year",
            dataIndex: "year",
            key: "year",
        },
        {
            title: "Total IGF Collected",
            dataIndex: "totalIgfDisplay",
            key: "totalIgfDisplay",
        },
    ];

    const computerizedBillingSystemColumns = [
        {
            title: "Availability of Computerized Billing System (YES/NO)",
            dataIndex: "billing",
            key: "billing"
        },
        {
            title: "Name of Software/System",
            dataIndex: "name",
            key: "name"
        },
        {
            title: "Functional (YES/NO)",
            dataIndex: "functional",
            key: "functional"
        },
        {
            title: "Reports",
            dataIndex: "report",
            key: "report",
            render: (value) => renderDpatEvidenceLink(value, "View Report"),
        }
    ];

    const billingIssuanceColumns = [
        {
            title: "Number of Properties on Roll",
            dataIndex: "propertyBill",
            key: "propertyBill"
        },
        {
            title: "Number of Bills Issued",
            dataIndex: "propertyBillIssued",
            key: "propertyBillIssued"
        },
        {
            title: "Date Submitted to Property Owner",
            dataIndex: "propertyBillDateSubmission",
            key: "propertyBillDateSubmission"
        },
        {
            title: "Number of Businesses on Roll",
            dataIndex: "businessBill",
            key: "businessBill"
        },
        {
            title: "Number of Bills Issued",
            dataIndex: "businessBillIssued",
            key: "businessBillIssued"
        },
        {
            title: "Date Submitted to Business Owner",
            dataIndex: "businessBillDateSubmission",
            key: "businessBillDateSubmission"
        }
    ];

    const followUpColumns = [
        { title: "No.", dataIndex: "no", key: "no" },
        { title: "Type of defaulter", dataIndex: "defaulter", key: "defaulter" },
        { title: "Type of action", dataIndex: "actionType", key: "actionType" },
        { title: "Date of Action", dataIndex: "actionDate", key: "actionDate" },
        { title: "Date of Payment", dataIndex: "paymentDate", key: "paymentDate" }
    ];

    return (
        <Comment
            data={issuance}
            year={analyticsYear}
            districtId={orgUnit}
            tableCommentedId={`pi2.0-2.1-${analyticsYear}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={analyticsYear}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
                      <MaxScoreDisplay score={maxScore} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 2.0-2.1i Actual Score" score={scorei} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 2.0-2.1ii Actual Score" score={scoreii} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 2.0-2.1iii Actual Score" score={scoreiii} />
                    </IndicatorScoreSection>

                    <Title level={5} style={{ marginTop: "20px" }}>
                        I- Evidence of Computerized Billing System & Utilisation
                    </Title>
                    <Table
                        columns={computerizedBillingSystemColumns}
                        dataSource={software}
                        rowKey="key"
                        pagination={false}
                        bordered
                    />

                    <Title level={5} style={{ marginTop: "20px" }}>
                        Evidence of Revenue Reports (Document Hub)
                    </Title>
                    <Table
                        columns={documentTableColumns}
                        dataSource={displayDocumentLinks}
                        rowKey="title"
                        pagination={false}
                        bordered
                    />

                    <Title level={5} style={{ marginTop: "20px" }}>
                        IGF Collection Comparison ({previousYear} vs {analyticsYear})
                    </Title>
                    <Table
                        columns={igfComparisonColumns}
                        dataSource={igfComparisonTableData}
                        rowKey="key"
                        pagination={false}
                        bordered
                    />

                    <div style={{ width: "100%", height: 260, marginTop: 16 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={igfChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year" />
                                <YAxis tickFormatter={(value) => formatGhanaCedis(value)} width={120} />
                                <Tooltip formatter={formatIgfTooltipValue} />
                                <Bar dataKey="total" fill="#1677ff" name="Total IGF Collected" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {!hideIssuanceTable && (
                        <>
                            <Title level={5} style={{ marginTop: "20px" }}>
                                II- Evidence of Issuance of Bills
                            </Title>
                            <Table
                                columns={billingIssuanceColumns}
                                dataSource={issuance}
                                pagination={false}
                                bordered
                            />
                        </>
                    )}

                    {showFollowUpTable && (
                        <>
                            <Title level={5} style={{ marginTop: "20px" }}>
                                {hideIssuanceTable
                                    ? "II- Evidence of follow-up action by Assembly on defaulters/ nonpayers"
                                    : "III- Evidence of follow-up action by Assembly on defaulters/ nonpayers"}
                            </Title>
                            <Table
                                columns={followUpColumns}
                                dataSource={followup}
                                pagination={false}
                                bordered
                            />
                        </>
                    )}
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
})

export default RateableRevenu;